// Monaco editor wrapper with a file-tab bar. Monaco is an imperative,
// non-React library, so this component owns a single editor instance for its
// lifetime and a Monaco *model* per open file: the user's program (editable)
// plus the bundled standard-library sources (read-only). A lot of a program's
// behaviour happens in the stdlib, so the debugger needs to open those files and
// point at a line inside them — hence the tabs.
//
// The active file is controlled by the parent (so selecting a debugger frame can
// switch to the file that frame runs in). The component publishes diagnostics on
// each edit of the program, draws the lock-plan annotations on the program, and
// highlights the current execution line in whichever file it belongs to.

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type * as MonacoNS from "monaco-editor";
import { analyzeSource } from "../../src/compiler.js";
import type { Diagnostic, TxnPlanEntry } from "../../src/compiler.js";
import {
  LANG_ID,
  THEME_DARK,
  THEME_LIGHT,
  registerLanguage,
  setNavigateHandler,
  spanToRange,
  uriForFile,
} from "../monaco-lang.js";
import { strategyLabel, strategyTooltip } from "../lockplan-view.js";

type Monaco = typeof MonacoNS;

/** The program tab's file id (the user's document has no stdlib `source`). */
export const PROGRAM_FILE = "";

export interface EditorHandle {
  /** Move the cursor to a location in the program and focus the editor. */
  revealPosition(line: number, col: number): void;
}

/** One standard-library source, shown as a read-only tab. */
export interface StdlibFile {
  id: string;
  src: string;
}

/** The current execution line to highlight, in a specific file. */
export interface Highlight {
  file: string;
  line: number;
}

interface EditorProps {
  monaco: Monaco;
  /** Program source to show. Changing it (e.g. picking an example) resets the buffer. */
  value: string;
  /** Tab label for the program file. */
  programLabel: string;
  onChange(value: string): void;
  onDiagnostics(diags: Diagnostic[]): void;
  /** Per-`txn` lock-plan decisions, drawn as inline annotations on each txn line. */
  txnPlan?: TxnPlanEntry[];
  /** Read-only standard-library files, shown as extra tabs. */
  stdlibFiles: StdlibFile[];
  /** Which file is active (`PROGRAM_FILE` or a stdlib id) — controlled by the parent. */
  activeFile: string;
  onActiveFileChange(id: string): void;
  /** The current execution line to highlight, or null. */
  highlight: Highlight | null;
}

function prefersDark(): boolean {
  return !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

// While the shared TextMate grammar is still loading (Shiki compiles its wasm
// asynchronously), the STM themes aren't defined yet, so fall back to Monaco's
// built-ins. Once `registerLanguage` resolves we switch to the STM themes.
function currentTheme(themesReady: boolean): string {
  if (!themesReady) return prefersDark() ? "vs-dark" : "vs";
  return prefersDark() ? THEME_DARK : THEME_LIGHT;
}

export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor(
  {
    monaco,
    value,
    programLabel,
    onChange,
    onDiagnostics,
    txnPlan,
    stdlibFiles,
    activeFile,
    onActiveFileChange,
    highlight,
  },
  ref
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<MonacoNS.editor.IStandaloneCodeEditor | null>(null);
  // One Monaco model per file id; created lazily and disposed on unmount.
  const modelsRef = useRef<Map<string, MonacoNS.editor.ITextModel>>(new Map());
  // The current-line highlight, tracked with the model it was applied to so it
  // can be cleared when the active file changes.
  const hlRef = useRef<{ model: MonacoNS.editor.ITextModel; ids: string[] } | null>(null);
  // Lock-plan annotations live on the program model, independent of active file.
  const planDecorationsRef = useRef<string[]>([]);
  // A cross-file jump (from the navigation bridge) records where to land; the
  // reveal happens once the target file's model is the active one.
  const pendingRevealRef = useRef<{ fileId: string; line: number; col: number } | null>(null);
  const flushRevealRef = useRef<() => void>(() => {});
  // Keep the latest callbacks/props reachable from listeners registered once on mount.
  const onChangeRef = useRef(onChange);
  const onDiagnosticsRef = useRef(onDiagnostics);
  const onActiveFileChangeRef = useRef(onActiveFileChange);
  const activeFileRef = useRef(activeFile);
  onChangeRef.current = onChange;
  onDiagnosticsRef.current = onDiagnostics;
  onActiveFileChangeRef.current = onActiveFileChange;
  activeFileRef.current = activeFile;

  useImperativeHandle(ref, (): EditorHandle => ({
    revealPosition(line, col) {
      const editor = editorRef.current;
      if (!editor) return;
      onActiveFileChange(PROGRAM_FILE);
      const model = modelsRef.current.get(PROGRAM_FILE);
      if (model) editor.setModel(model);
      editor.revealLineInCenter(line);
      editor.setPosition({ lineNumber: line, column: col });
      editor.focus();
    },
  }));

  // Analyze the program buffer, publish markers, and report diagnostics up.
  const check = (model: MonacoNS.editor.ITextModel): void => {
    const svc = analyzeSource(model.getValue());
    monaco.editor.setModelMarkers(
      model,
      LANG_ID,
      svc.diagnostics.map((d) => ({
        severity: d.severity === "error" ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
        message: d.message,
        ...spanToRange(d.span),
      }))
    );
    onDiagnosticsRef.current(svc.diagnostics);
  };

  // Look up (creating on first use) the model for a file id, under the shared URI
  // scheme so it's the same buffer the definition/reference providers resolve to.
  // The program model is editable; stdlib models hold static file content.
  const modelFor = (id: string): MonacoNS.editor.ITextModel => {
    const uri = uriForFile(monaco, id);
    const existing = monaco.editor.getModel(uri);
    if (existing) return existing;
    const content = id === PROGRAM_FILE ? value : stdlibFiles.find((f) => f.id === id)?.src ?? "";
    const model = monaco.editor.createModel(content, LANG_ID, uri);
    modelsRef.current.set(id, model);
    return model;
  };

  // Create the editor once, on mount.
  useEffect(() => {
    let themesReady = false;
    registerLanguage(monaco)
      .then(() => {
        themesReady = true;
        monaco.editor.setTheme(currentTheme(themesReady));
      })
      .catch((err) => console.error("STM syntax highlighting failed to load", err));

    const programModel = modelFor(PROGRAM_FILE);

    const editor = monaco.editor.create(hostRef.current!, {
      model: programModel,
      theme: currentTheme(themesReady),
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: 13,
      lineHeight: 20,
      scrollBeyondLastLine: false,
      tabSize: 2,
      renderLineHighlight: "line",
      wordBasedSuggestions: "off",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    });
    editorRef.current = editor;

    let handle: ReturnType<typeof setTimeout>;
    const changeSub = editor.onDidChangeModelContent(() => {
      // Only the program is editable; ignore edits while a read-only file is shown.
      if (editor.getModel() !== modelsRef.current.get(PROGRAM_FILE)) return;
      onChangeRef.current(programModel.getValue());
      clearTimeout(handle);
      handle = setTimeout(() => check(programModel), 300);
    });

    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const onThemeChange = () => monaco.editor.setTheme(currentTheme(themesReady));
    mq?.addEventListener?.("change", onThemeChange);

    // Reveal a pending cross-file jump once its file's model is the active one.
    flushRevealRef.current = () => {
      const pr = pendingRevealRef.current;
      if (!pr) return;
      const model = monaco.editor.getModel(uriForFile(monaco, pr.fileId));
      if (!model || editor.getModel() !== model) return; // wait for the tab to switch
      editor.revealLineInCenter(pr.line);
      editor.setPosition({ lineNumber: pr.line, column: pr.col });
      editor.focus();
      pendingRevealRef.current = null;
    };

    // The cross-file navigation bridge: switch to the target file's tab, then
    // reveal the position (immediately if already there).
    setNavigateHandler((fileId, line, col) => {
      pendingRevealRef.current = { fileId, line, col };
      if (fileId === activeFileRef.current) flushRevealRef.current();
      else onActiveFileChangeRef.current(fileId);
    });

    check(programModel);

    return () => {
      clearTimeout(handle);
      changeSub.dispose();
      mq?.removeEventListener?.("change", onThemeChange);
      setNavigateHandler(null);
      editor.dispose();
      for (const m of modelsRef.current.values()) m.dispose();
      modelsRef.current.clear();
      editorRef.current = null;
    };
    // Mount-only: `value` is applied imperatively below, callbacks via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monaco]);

  // Apply external program `value` changes (example switch) without recreating
  // the editor or clobbering the buffer while the user is typing.
  useEffect(() => {
    const model = modelsRef.current.get(PROGRAM_FILE);
    if (model && model.getValue() !== value) {
      model.setValue(value);
      check(model);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Swap the visible model when the active file changes; stdlib files are read-only.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const model = modelFor(activeFile);
    if (editor.getModel() !== model) editor.setModel(model);
    editor.updateOptions({ readOnly: activeFile !== PROGRAM_FILE });
    // A cross-file jump switched the tab; now that its model is active, land on it.
    flushRevealRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile, stdlibFiles]);

  // Draw the current-execution line highlight in the file it belongs to,
  // switching the model if needed so the line is actually visible.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    // Clear any previous highlight (possibly on another model).
    if (hlRef.current) {
      hlRef.current.model.deltaDecorations(hlRef.current.ids, []);
      hlRef.current = null;
    }
    if (!highlight || highlight.line < 1) return;
    const model = modelFor(highlight.file);
    const ids = model.deltaDecorations(
      [],
      [
        {
          range: new monaco.Range(highlight.line, 1, highlight.line, 1),
          options: {
            isWholeLine: true,
            className: "trace-line-hl",
            marginClassName: "trace-line-hl-margin",
          },
        },
      ]
    );
    hlRef.current = { model, ids };
    if (editor.getModel() === model) editor.revealLineInCenterIfOutsideViewport(highlight.line);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, activeFile]);

  // Draw each txn's lock-plan decision inline on the program model: a badge after
  // the `txn` line and a hover explaining why it locks or runs optimistically.
  useEffect(() => {
    const model = modelsRef.current.get(PROGRAM_FILE);
    if (!model) return;
    const entries = txnPlan ?? [];
    const lineCount = model.getLineCount();
    planDecorationsRef.current = model.deltaDecorations(
      planDecorationsRef.current,
      entries.flatMap((e) => {
        const lock = e.strategy.kind === "lock";
        const line = e.span.start.line;
        if (line < 1 || line > lineCount) return [];
        const endCol = model.getLineMaxColumn(line);
        return [
          {
            range: new monaco.Range(line, Math.max(1, endCol - 1), line, endCol),
            options: {
              linesDecorationsClassName: lock ? "lp-gutter-lock" : "lp-gutter-occ",
              after: {
                content: `   ${lock ? "🔒" : "⚡"} ${strategyLabel(e.strategy)}`,
                inlineClassName: lock ? "lp-after-lock" : "lp-after-occ",
              },
              hoverMessage: { value: strategyTooltip(e.strategy) },
            },
          },
        ];
      })
    );
  }, [txnPlan, monaco]);

  const tabs = [{ id: PROGRAM_FILE, label: programLabel }, ...stdlibFiles.map((f) => ({ id: f.id, label: f.id }))];

  return (
    <div className="source-view">
      <div className="src-tabs" role="tablist" aria-label="source files">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeFile === t.id}
            className={"src-tab" + (activeFile === t.id ? " on" : "")}
            onClick={() => onActiveFileChange(t.id)}
          >
            {t.label}
            {t.id !== PROGRAM_FILE && <span className="src-tab-lib">lib</span>}
          </button>
        ))}
      </div>
      <div id="editor" ref={hostRef} />
    </div>
  );
});
